import { useMemo, useState } from "react";
import { isAfter, isBefore, parse } from "date-fns";

// * If dataWithExports is true we use .answer to get the values that are displayed on the table
function useSortedData(data, initialSortColumn = "submitted_at", initialSortDirection = "asc", dataWithExports = false) {
  const [sortColumn, setSortColumn] = useState(initialSortColumn);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  function handleRequestSort(event, property) {
    const isAsc = sortColumn === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(property);
  };

  // Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
  // stableSort() brings sort stability to non-modern browsers (notably IE11). If you
  // only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
  // with exampleArray.slice().sort(exampleComparator)
  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const sortDirection = comparator(a[0], b[0]);
      if (sortDirection !== 0) {
        return sortDirection;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function descendingComparator(a, b, sortColumn) {
    // TODO: Map all of the reducers data to match creationDate here
    if (sortColumn === "submitted_at" || sortColumn === "creationDate" || sortColumn === "compare_submitted_at") {
      // TODO: All incoming date string formats should be of the type "yyyy-MM-dd'T'HH:mm:ssX". Presentation can be parsed when displaying on the table
      const dateA = dataWithExports ? parse(a[sortColumn].answer, "dd-MM-yyyy HH:mm", new Date()) : parse(a[sortColumn], "yyyy-MM-dd'T'HH:mm:ssX", new Date())
      const dateB = dataWithExports ? parse(b[sortColumn].answer, "dd-MM-yyyy HH:mm", new Date()) : parse(b[sortColumn], "yyyy-MM-dd'T'HH:mm:ssX", new Date())

      if (isAfter(dateB, dateA)) {
        return 1;
      }
      if (isBefore(dateB, dateA)) {
        return -1;
      }
      return 0;
    } else  if (sortColumn === "packageLabel" || sortColumn === "businessPackage") {

      const valueA = dataWithExports 
      ? a[sortColumn]?.answer 
      : (sortColumn === "packageLabel" ? a[sortColumn] : a[sortColumn]?.name) || "\uFFFF";
      
      const valueB = dataWithExports 
      ? b[sortColumn]?.answer 
      : (sortColumn === "packageLabel" ? b[sortColumn] : b[sortColumn]?.name) || "\uFFFF";

      return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
    }
    else {
      const columnValueA = dataWithExports ? a[sortColumn].answer : a[sortColumn];
      const columnValueB = dataWithExports ? b[sortColumn].answer : b[sortColumn];

      if (columnValueA < columnValueB) {
        return 1;
      }
      if (columnValueA > columnValueB) {
        return -1;
      }

      if (a.creationDate && b.creationDate) {
        // * If values are equal, compare by `creationDate`
        const dateA = parse(a.creationDate, "yyyy-MM-dd'T'HH:mm:ssX", new Date());
        const dateB = parse(b.creationDate, "yyyy-MM-dd'T'HH:mm:ssX", new Date());

        // * Latest date takes precedence
        if (isAfter(dateB, dateA)) {
          return -1;
        }
        if (isBefore(dateB, dateA)) {
          return 1;
        }
      }

      return 0;
    }
  }

  function getComparator(sortDirection, sortColumn) {
    return sortDirection === 'desc'
      ? (a, b) => descendingComparator(a, b, sortColumn)
      : (a, b) => -descendingComparator(a, b, sortColumn);
  }

  const sortedData = useMemo(
    () => {
      if (data.length > 0) {
        return stableSort(data, getComparator(sortDirection, sortColumn));
      }
      return []
    },
    [data, sortDirection, sortColumn],
  );

  return {
    sortedData,
    sortColumn,
    sortDirection,
    handleRequestSort,
  };
}

export default useSortedData;
